/**
 * QueueDisplay — the PUBLIC display-board screen (route `queue.display`,
 * param b=<boardId>): the full-screen "now serving" view an admin puts on a
 * lobby TV, independent of Studio. Content is the board's ordered block list
 * (composed in the Journey Designer; legacy boards without blocks render the
 * layout-derived defaults); themes + queuer-display format from the board
 * config; polls getPublicDisplay every 5s.
 *
 * Orientation (2026-09-02, operator): a board is mounted either way — landscape
 * on a wall bracket, portrait turned on its side in an entrance — so the layout
 * follows the real screen instead of assuming a wide one. Landscape reads across
 * in up to three columns; portrait stacks the cards full width and puts the clock
 * on its own line, which is what the Journey Designer's Landscape / Portrait
 * toggle previews.
 */
import { useEffect, useState } from "react";
import { useWfResource } from "../workforce/useWorkforce";
import { useNav } from "../../shell/router";
import { getPublicDisplay, type PublicDisplay } from "./queues-api";
import { BoardQueueCard, boardColors, defaultBoardBlocks, useBoardPager } from "./board-blocks";

/** Columns for the queue grid (2026-09-02, operator). Deliberately few: card
 *  height is a function of column width, so more columns is not a way to fit
 *  more queues — narrow cards wrap their block content and grow much taller.
 *  Queues beyond one screenful cycle instead (useBoardPager). Kept in step with
 *  `boardColumnsFor` in the Journey Designer so the preview does not lie. */
function displayColumns(n: number, portrait: boolean) {
  if (n <= 0) return 1;
  return portrait ? (n <= 6 ? 1 : 2) : Math.min(n, 3);
}

import { useT } from "@v0y4ge/i18n-runtime";
export default function QueueDisplay() {
  const t = useT();
  const nav = useNav();
  /* The mounted screen's own orientation — no configuration, because a TV knows
     which way up it is and an operator should not have to tell it twice. */
  const [portrait, setPortrait] = useState(() =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia("(orientation: portrait)").matches
      : false);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(orientation: portrait)");
    const on = (e: MediaQueryListEvent | MediaQueryList) => setPortrait((e as MediaQueryList).matches);
    on(mq);
    /* addListener is the Safari-on-a-TV-box fallback: several smart-TV browsers
       still ship a MediaQueryList without addEventListener. */
    if (mq.addEventListener) { mq.addEventListener("change", on as (e: MediaQueryListEvent) => void); return () => mq.removeEventListener("change", on as (e: MediaQueryListEvent) => void); }
    mq.addListener(on as (e: MediaQueryListEvent) => void);
    return () => mq.removeListener(on as (e: MediaQueryListEvent) => void);
  }, []);
  const boardId = (nav.params?.b ?? "") as string;
  const boardToken = (nav.params?.t ?? "") as string;
  const { data, loading, error } = useWfResource<PublicDisplay | null>(
    `qdisplay|${boardId}|${boardToken}`,
    () => (boardId ? getPublicDisplay(boardId, boardToken) : Promise.resolve(null)),
    { pollMs: 5_000 });

  const dark = data?.board.theme !== "light";
  const C = boardColors(dark);
  /* Queues beyond one screenful cycle rather than sitting below the bottom edge
     of the TV where nobody can see them (2026-09-02, operator). Hook order:
     called before the early returns below. */
  const queueList = data?.queues ?? [];
  const cols = displayColumns(queueList.length, portrait);
  const pager = useBoardPager(queueList, cols, portrait ? 18 : 24);

  const frame = (children: React.ReactNode) => (
    <div style={{ height: "100vh", background: C.bg, color: C.fg, fontFamily: C.font, display: "flex", flexDirection: "column", padding: portrait ? "30px 26px 24px" : "36px 44px 28px", boxSizing: "border-box", overflow: "hidden" }}>
      {children}
    </div>
  );
  if (!boardId) return frame(<div style={{ margin: "auto", fontSize: 20, color: C.soft }}>{t("queues.display.text", "This display link is missing its board id.")}</div>);
  if (error) return frame(<div style={{ margin: "auto", fontSize: 20, color: C.soft }}>{t("queues.display.text2", "Display unavailable —")}{" "}{error}</div>);
  if (loading && !data) return frame(<div style={{ margin: "auto", fontSize: 20, color: C.soft }}>{t("queues.display.text3", "Loading…")}</div>);
  const d = data!;
  const blocks = d.board.blocks?.length ? d.board.blocks : defaultBoardBlocks(d.board.layout);
  const nameFormat = d.board.nameFormat ?? "first-initial";

  return frame(
    <>
      <div style={{ display: "flex", alignItems: portrait ? "flex-start" : "baseline", flexDirection: portrait ? "column" : "row", gap: portrait ? 3 : 14, marginBottom: portrait ? 22 : 28, flexShrink: 0 }}>
        <span style={{ fontSize: portrait ? 30 : 26, fontWeight: 800, letterSpacing: -0.3 }}>{d.org}</span>
        <span style={{ fontSize: portrait ? 17 : 15, color: C.soft }}>{d.board.name}</span>
        <span style={{ marginLeft: portrait ? 0 : "auto", marginTop: portrait ? 4 : 0, fontSize: portrait ? 20 : 14, fontWeight: portrait ? 700 : 400, color: C.soft }}>{new Date(d.asOf).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</span>
      </div>
      <div ref={pager.gridRef} style={{ flex: 1, minHeight: 0, display: "grid", gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gridAutoRows: "min-content", alignContent: "start", gap: portrait ? 18 : 24, overflow: "hidden" }}>
        {pager.visible.map((q, i) => (
          <div key={q.id} ref={i === 0 ? pager.cardRef : undefined} style={{ display: "grid", minWidth: 0 }}>
            <BoardQueueCard q={q} blocks={blocks} C={C} dark={dark} nameFormat={nameFormat} showWait={d.board.showWaitEstimates} />
          </div>
        ))}
      </div>
      <div style={{ marginTop: portrait ? 20 : 26, textAlign: "center", fontSize: portrait ? 19 : 17, color: C.soft, flexShrink: 0 }}>{d.board.message}</div>
      {/* Page dots: a waiting customer needs to know another page of queues is
          coming rather than wonder where theirs went. */}
      {pager.pages > 1 ? (
        <div style={{ marginTop: 14, display: "flex", justifyContent: "center", gap: 9, flexShrink: 0 }}>
          {Array.from({ length: pager.pages }).map((_, i) => (
            <span key={i} style={{ width: 10, height: 10, borderRadius: 999, background: i === pager.page ? C.fg : C.border }} />
          ))}
        </div>
      ) : null}
    </>
  );
}
